import { BridgesIsland } from '../interfaces/bridges-island';
import { BridgesPuzzleConfig } from '../interfaces/bridges-puzzle-config';
import {
  BridgesMap,
  computeNeighborPairs,
  currentDegree,
  serializeKey,
} from '../geometry/bridges-geometry';

export class BridgesPuzzleConfigError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'BridgesPuzzleConfigError';
  }
}

export interface ParsedBridgesPuzzle {
  islands: BridgesIsland[];
  puzzleID: string;
  /**
   * The one correct arrangement, when the puzzle file ships it. `bridges-generator`
   * discards any candidate with `multipleSolutions`, so where this exists it is
   * unique and a placed bridge that differs from it is definitively wrong.
   * Absent for the legacy node form, which carries no solution at all.
   */
  solution?: BridgesMap;
}

export function parseBridgesPuzzleConfig(raw: BridgesPuzzleConfig): ParsedBridgesPuzzle {
  if (!raw || typeof raw !== 'object') {
    throw new BridgesPuzzleConfigError('Bridges puzzle config is missing or not an object');
  }

  const anyRaw = raw as { puzzle?: unknown; id?: unknown; solution?: unknown };
  const puzzle = anyRaw.puzzle;

  if (Array.isArray(puzzle) && puzzle.every((row) => Array.isArray(row))) {
    return parseGridForm(
      puzzle as number[][],
      typeof anyRaw.id === 'string' ? anyRaw.id : undefined,
      anyRaw.solution
    );
  }

  if (puzzle && typeof puzzle === 'object') {
    return parseLegacyForm(puzzle as { game?: { nodes?: unknown }; id?: unknown });
  }

  throw new BridgesPuzzleConfigError('Unrecognized bridges puzzle config shape');
}

/** Symbols `bridges-generator` writes between two islands in the solution grid. */
const SOLUTION_HORIZONTAL: Record<string, number> = { '-': 1, '=': 2 };
const SOLUTION_VERTICAL: Record<string, number> = { '|': 1, $: 2 };

/**
 * Turns the solution grid into the same keyed map the board keeps its own
 * bridges in, so the two can be compared slot by slot.
 *
 * Returns undefined rather than a partial map whenever anything does not line
 * up — wrong shape, an out-of-bounds read, or a solution whose island degrees
 * disagree with the puzzle's numbers. A mismatched solution would mark a
 * correctly played board as wrong everywhere, which is far worse than simply
 * having no solution to compare against.
 */
function parseSolutionGrid(solution: unknown, islands: BridgesIsland[]): BridgesMap | undefined {
  if (!Array.isArray(solution) || !solution.every((row) => Array.isArray(row))) return undefined;
  const grid = solution as string[][];
  const map: BridgesMap = {};

  for (const key of computeNeighborPairs(islands)) {
    const a = islands.find((n) => n.id === key.a);
    const b = islands.find((n) => n.id === key.b);
    if (!a || !b) return undefined;

    let row: number;
    let col: number;
    let symbols: Record<string, number>;
    if (a.y === b.y) {
      // Adjacent islands leave no cell to carry a symbol; no bridge can be drawn
      // between them either, so the slot is simply empty.
      if (Math.abs(a.x - b.x) < 2) {
        map[serializeKey(key)] = 0;
        continue;
      }
      row = a.y;
      col = Math.min(a.x, b.x) + 1;
      symbols = SOLUTION_HORIZONTAL;
    } else {
      if (Math.abs(a.y - b.y) < 2) {
        map[serializeKey(key)] = 0;
        continue;
      }
      row = Math.min(a.y, b.y) + 1;
      col = a.x;
      symbols = SOLUTION_VERTICAL;
    }

    const cell = grid[row]?.[col];
    if (cell === undefined) return undefined;
    map[serializeKey(key)] = symbols[String(cell)] ?? 0;
  }

  for (const n of islands) {
    if (currentDegree(map, n.id) !== n.requiredBridges) return undefined;
  }
  return map;
}

function parseGridForm(
  grid: number[][],
  rawId: string | undefined,
  rawSolution?: unknown
): ParsedBridgesPuzzle {
  const islands: BridgesIsland[] = [];
  for (let r = 0; r < grid.length; r++) {
    const row = grid[r];
    if (!Array.isArray(row)) {
      throw new BridgesPuzzleConfigError(`Bridges grid row ${r} is not an array`);
    }
    for (let c = 0; c < row.length; c++) {
      const val = row[c];
      if (typeof val === 'number' && val > 0) {
        islands.push({ id: `${r},${c}`, x: c, y: r, requiredBridges: val });
      }
    }
  }
  if (islands.length === 0) {
    throw new BridgesPuzzleConfigError('Bridges grid contains no islands');
  }
  const puzzleID = rawId ?? `bridges-grid-${hashIslands(islands)}`;
  return { islands, puzzleID, solution: parseSolutionGrid(rawSolution, islands) };
}

function parseLegacyForm(puzzle: { game?: { nodes?: unknown }; id?: unknown }): ParsedBridgesPuzzle {
  const game = puzzle.game;
  if (!game || typeof game !== 'object' || !Array.isArray(game.nodes)) {
    throw new BridgesPuzzleConfigError('Bridges legacy config is missing puzzle.game.nodes array');
  }
  const islands = (game.nodes as unknown[]).map((n, i) => coerceIsland(n, i));
  if (islands.length === 0) {
    throw new BridgesPuzzleConfigError('Bridges legacy config contains no islands');
  }
  const puzzleID = typeof puzzle.id === 'string' ? puzzle.id : `bridges-legacy-${hashIslands(islands)}`;
  return { islands, puzzleID };
}

// Deterministic 32-bit FNV-1a hash over the canonical island serialization.
// Same set of islands → same puzzleID across reloads and across devices.
function hashIslands(islands: BridgesIsland[]): string {
  const canonical = islands
    .map((n) => `${n.x},${n.y},${n.requiredBridges}`)
    .sort()
    .join('|');
  let h = 0x811c9dc5;
  for (let i = 0; i < canonical.length; i++) {
    h ^= canonical.charCodeAt(i);
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return h.toString(16).padStart(8, '0');
}

function coerceIsland(raw: unknown, index: number): BridgesIsland {
  if (!raw || typeof raw !== 'object') {
    throw new BridgesPuzzleConfigError(`Bridges legacy node #${index} is not an object`);
  }
  const node = raw as { id?: unknown; x?: unknown; y?: unknown; requiredBridges?: unknown };
  if (
    typeof node.id !== 'string' ||
    typeof node.x !== 'number' ||
    typeof node.y !== 'number' ||
    typeof node.requiredBridges !== 'number'
  ) {
    throw new BridgesPuzzleConfigError(`Bridges legacy node #${index} is missing required fields`);
  }
  return { id: node.id, x: node.x, y: node.y, requiredBridges: node.requiredBridges };
}
